/**
 * LanguageToggle (P127 / brand P2.11).
 *
 * Compact EN / ع switch in the AppShell header. Switching to Arabic
 * flips the document to `dir="rtl"` (handled in shared/i18n) and renders
 * the scaffold catalogue. The preference persists in localStorage.
 *
 * G4: the control is HIDDEN while the Arabic catalogue is still the
 * scaffold. Offering it then does not advertise a roadmap item, it
 * advertises a broken feature -- a reader who clicks gets right-to-left
 * layout and English in guillemets, and cannot tell "not translated
 * yet" from "translation is broken". The mechanism still ships and is
 * still proven by the tests; it simply is not offered until there is
 * Arabic behind it.
 *
 * It returns the moment a translator lands any real strings, because
 * AR_IS_OFFERED is derived from the catalogue rather than declared.
 */
import { useTranslation } from "react-i18next";

import { AR_IS_OFFERED, SUPPORTED_LANGS, setLanguage } from "shared/i18n";

const LABEL: Record<string, string> = { en: "EN", ar: "ع" };

export function LanguageToggle() {
  const { i18n } = useTranslation();
  const current = i18n.language?.startsWith("ar") ? "ar" : "en";

  // Nothing to choose between: one language, so no switch.
  if (!AR_IS_OFFERED) return null;

  // P214.2 (Observatory): a borderless mono "EN · ع" toggle -- active
  // language bold in brass, inactive in sky.nav, a mid-dot separator.
  return (
    <div
      data-testid="language-toggle"
      className="inline-flex items-center gap-1.5 font-mono text-[11px]"
    >
      {SUPPORTED_LANGS.map((lng, idx) => {
        const active = lng === current;
        return (
          <span key={lng} className="inline-flex items-center gap-1.5">
            {idx > 0 && <span className="text-sky-dim">·</span>}
            <button
              type="button"
              data-testid={`lang-${lng}`}
              data-active={active ? "true" : "false"}
              aria-pressed={active}
              onClick={() => setLanguage(lng)}
              className={
                active
                  ? "font-bold text-brand-brass"
                  : "text-sky-nav hover:text-brand-cream"
              }
            >
              {LABEL[lng]}
            </button>
          </span>
        );
      })}
    </div>
  );
}
